import { Fragment, type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { PLAN_PRICES_VND, applyPercentOff, perMonthFromAnnualVnd, ZALO_OA_URL } from '@repo/shared-types';
import type { PromoCodeCheck, PromoCodeReason } from '@repo/shared-types';
import { cn } from '@repo/ui/src/lib/utils';
import { Button } from '@repo/ui/src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@repo/ui/src/components/ui/dialog';
import { useUpgradeDialogStore } from '../../stores/upgradeDialogStore';
import { useSubscription, useUpgradeSubscription } from '../../hooks/useSubscription';
import { useCan } from '../../hooks/useCan';
import { PromoCodeRow } from './PromoCodeRow';

type TierCode = 'FREE' | 'PLUS' | 'PRO' | 'CENTER';
type BillingCycle = 'MONTHLY' | 'ANNUAL';

type PurchasableCode = 'PLUS' | 'PRO' | 'CENTER';

// Four columns, matching the landing. The old fifth "Liên hệ" column made every
// other column cramped to serve the rare enterprise conversation; that band is
// now the footer row below the table, where it costs nobody any width.
const TIER_CODES: TierCode[] = ['FREE', 'PLUS', 'PRO', 'CENTER'];

// PLAN_PRICES_VND (packages/shared-types) mirrors apps/api/src/lib/seedPlans.ts,
// the source of truth for what customers are charged; seedPlans.test.ts asserts
// the two stay equal. Annual is monthly − 20% (2026-08-26), not ten months.
const PRICE_VND_MONTHLY: Record<PurchasableCode, number> = {
  PLUS: PLAN_PRICES_VND.PLUS.monthly,
  PRO: PLAN_PRICES_VND.PRO.monthly,
  CENTER: PLAN_PRICES_VND.CENTER.monthly,
};
const PRICE_VND_ANNUAL: Record<PurchasableCode, number> = {
  PLUS: PLAN_PRICES_VND.PLUS.annual,
  PRO: PLAN_PRICES_VND.PRO.annual,
  CENTER: PLAN_PRICES_VND.CENTER.annual,
};

function fmtVnd(n: number, lang: string) {
  const locale = lang.startsWith('vi') ? 'vi-VN' : 'en-US';
  return `${n.toLocaleString(locale)}đ`;
}

// Rows rendered as a simple on/off check across tiers.
type BooleanRowKey = 'core' | 'qrPayment' | 'noWatermark' | 'bookkeeping' | 'aiImport' | 'sessionNotes' | 'progressLink' | 'payroll' | 'multiBranch';
// Rows rendered as free-form text (numbers / "unlimited") across tiers.
type TextRowKey = 'students' | 'seats' | 'autoReconcile' | 'aiCredits';
// A built-but-not-shipped row: never a check/X purchase affordance, only "off"
// (❌) or "soon" (a "Sắp có" chip). Exactly ONE qualifies now — ZNS, waiting on
// the OA purchase. multiBranch graduated to a real boolean row when THI-137
// shipped (2026-08-27). The test a row must pass to live here is unchanged:
// the timeline is entirely ours.
type ComingSoonRowKey = 'zns';
type RowKey = BooleanRowKey | TextRowKey | ComingSoonRowKey;

const BOOLEAN_ROWS: Record<BooleanRowKey, Record<TierCode, boolean>> = {
  // The spine every plan ships: attendance, timetable, month close.
  core: { FREE: true, PLUS: true, PRO: true, CENTER: true },
  qrPayment: { FREE: true, PLUS: true, PRO: true, CENTER: true },
  noWatermark: { FREE: false, PLUS: false, PRO: true, CENTER: true },
  bookkeeping: { FREE: false, PLUS: false, PRO: true, CENTER: true },
  aiImport: { FREE: false, PLUS: true, PRO: true, CENTER: true },
  // THI-105/196: notes, the timeline and monthly reviews are on every plan
  // (the accumulated data is the upgrade pull); only the parent link and the
  // homework photos are Pro and up (`Plan.allowsProgressLink`).
  sessionNotes: { FREE: true, PLUS: true, PRO: true, CENTER: true },
  progressLink: { FREE: false, PLUS: false, PRO: true, CENTER: true },
  payroll: { FREE: false, PLUS: false, PRO: true, CENTER: true },
  multiBranch: { FREE: false, PLUS: false, PRO: false, CENTER: true },
};

type ComingSoonStatus = 'off' | 'soon';
const COMING_SOON_ROWS: Record<ComingSoonRowKey, Record<TierCode, ComingSoonStatus>> = {
  zns: { FREE: 'off', PLUS: 'off', PRO: 'soon', CENTER: 'soon' },
};

const ROW_ORDER: RowKey[] = [
  'students',
  'seats',
  'core',
  'autoReconcile',
  'noWatermark',
  'qrPayment',
  'aiImport',
  'aiCredits',
  'sessionNotes',
  'progressLink',
  'bookkeeping',
  'payroll',
  'zns',
  'multiBranch',
];

// Maps the `feature` value set by a server 403 (FEATURE_LOCKED /
// TIER_LIMIT_REACHED) onto the row it should highlight.
const FEATURE_TO_ROW: Record<string, RowKey> = {
  students: 'students',
  extras: 'bookkeeping',
  bookkeeping: 'bookkeeping',
  aiImport: 'aiImport',
  seat: 'seats',
  payroll: 'payroll',
  branches: 'multiBranch',
  progressLink: 'progressLink',
  // THI-179: send-zalo's 403 now carries `feature: 'zns'` (it used to omit
  // the field entirely) — without this entry the dialog opened with nothing
  // highlighted.
  zns: 'zns',
};

const TIER_RANK: Record<TierCode, number> = { FREE: 0, PLUS: 1, PRO: 2, CENTER: 3 };
const PURCHASABLE_CODES: PurchasableCode[] = ['PLUS', 'PRO', 'CENTER'];

export function UpgradeDialog() {
  const { t, i18n } = useTranslation('billing');
  const navigate = useNavigate();
  const isOpen = useUpgradeDialogStore((s) => s.isOpen);
  const feature = useUpgradeDialogStore((s) => s.feature);
  const close = useUpgradeDialogStore((s) => s.close);
  const { data: sub } = useSubscription();
  const canManageBilling = useCan('manage:settings');
  const upgrade = useUpgradeSubscription();
  // Null until the teacher picks. Annual is what we push (doc §8), but someone
  // already on a monthly term defaults to monthly: a mid-cycle upgrade is only
  // prorated within a matching term, so defaulting them to annual would turn a
  // small top-up into a year's invoice.
  const [pickedCycle, setPickedCycle] = useState<BillingCycle | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentCode = (sub?.plan.code ?? 'FREE') as TierCode;
  const onTrial = sub?.status === 'TRIALING';
  const onPaidTerm = !onTrial && currentCode !== 'FREE';
  const cycle = pickedCycle ?? (onPaidTerm ? (sub?.billingCycle ?? 'ANNUAL') : 'ANNUAL');
  // A purchase already scheduled must land before another is bought on top of it
  // (the API rejects the second one), so don't offer any.
  const scheduled = sub?.scheduledChange ?? null;

  const highlightedRow = feature ? FEATURE_TO_ROW[feature] : undefined;

  /**
   * What a tier's column offers this teacher. Downgrades are deliberately absent:
   * changing to a cheaper plan mid-term has no path yet (see the roadmap) — the
   * route today is to stop the next invoice, lapse to Free, and buy the lower plan.
   */
  function ctaFor(code: TierCode): 'buy' | 'current' | 'none' {
    if (code === 'FREE') return 'none';
    if (!canManageBilling || scheduled) return 'none';
    if (onTrial) return 'buy'; // a trial can convert to any paid plan
    if (code === currentCode) return 'current';
    return TIER_RANK[code] > TIER_RANK[currentCode] ? 'buy' : 'none';
  }

  // THI-200: the promo code row is one instance for the whole compare table
  // (see PromoCodeRow), so it needs ONE plan/cycle context to check the code
  // against. The cheapest tier a teacher could actually buy right now is the
  // sensible default — usually the same tier the dialog was opened to push.
  const showPromoRow = PURCHASABLE_CODES.some((c) => ctaFor(c) === 'buy');
  const contextPlan: PurchasableCode = PURCHASABLE_CODES.find((c) => ctaFor(c) === 'buy') ?? 'PRO';
  // An accepted code is not tied to a plan or a cycle here: the teacher can
  // buy ANY plan the code covers, and PromoCodeRow re-checks the code itself
  // whenever the cycle toggle moves (an earlier version keyed this on the
  // cycle and silently dropped the code on a switch, so the public campaign
  // applied instead of the richer code).
  const [promo, setPromo] = useState<{ code: string; check: Extract<PromoCodeCheck, { valid: true }> } | null>(null);
  const activePromo = promo;
  const offer = sub?.offer ?? null;

  // Fix wave (Important #1): shows the code's own "Kỳ đầu … với {name}" line
  // under EVERY plan the winning promotion actually covers (`check.planCodes`
  // / `.cycles` — see PromoCodeCheck's doc comment), not just the one plan the
  // code happened to be checked against. Promise and charge now stay in step:
  // a plan this line appears under is a plan the accepted code (or the
  // campaign that beat it) will actually discount when bought.
  function offerLineFor(code: PurchasableCode): { pct: number; name: string } | null {
    if (activePromo && activePromo.check.planCodes.includes(code) && activePromo.check.cycles.includes(cycle)) {
      return { pct: activePromo.check.percentOff, name: activePromo.check.name };
    }
    if (offer && offer.planCodes.includes(code) && offer.cycles.includes(cycle)) {
      return { pct: offer.percentOff, name: offer.name };
    }
    return null;
  }

  // Fix wave: the accepted code is sent for WHICHEVER plan is bought, not
  // only the `contextPlan` it was checked against — the old guard
  // (`planCode === contextPlan`) silently dropped an accepted code on every
  // other tier's CTA.
  //
  // Fix wave 2 (regression fix): sending the code UNCONDITIONALLY (wave 1)
  // meant a code that only covers PRO, clicked on CENTER, got a hard 400 —
  // even when an eligible campaign would otherwise have discounted CENTER on
  // its own. `check.planCodes`/`.cycles` are now the CODE's OWN coverage
  // (server fix, `GET /subscription/promo/:code`), so the code is sent ONLY
  // when it covers the clicked plan AND the selected cycle; otherwise it is
  // simply omitted (never sent), the same inline sentence explains why
  // ("Mã này không áp dụng cho gói/chu kỳ bạn đang chọn."), and the purchase
  // proceeds — the server still applies an eligible campaign on its own. The
  // PROMO_* catch below stays for genuine server-side rejections of a code
  // that WAS sent (STATUS, CAP, ALREADY_USED — coverage isn't the client's
  // call to make for those).
  const [promoError, setPromoError] = useState<string | null>(null);
  const handleUpgrade = async (planCode: PurchasableCode) => {
    setError(null);
    setPromoError(null);
    let promoCode: string | undefined;
    if (activePromo) {
      const coversPlan = activePromo.check.planCodes.includes(planCode);
      const coversCycle = activePromo.check.cycles.includes(cycle);
      if (coversPlan && coversCycle) {
        promoCode = activePromo.code;
      } else {
        setPromoError(t(`offer.reason.${coversPlan ? 'CYCLE' : 'PLAN'}`));
      }
    }
    try {
      const res = await upgrade.mutateAsync({ planCode, billingCycle: cycle, promoCode });
      close();
      navigate(`/settings/billing/invoices/${res.subscriptionInvoiceId}`);
    } catch (err) {
      const code = (err as Error & { code?: string }).code;
      if (code?.startsWith('PROMO_')) {
        const reason = code.slice('PROMO_'.length) as PromoCodeReason;
        setPromoError(t(`offer.reason.${reason}`));
      } else {
        setError((err as Error).message);
      }
    }
  };

  const tierLabel: Record<TierCode, string> = {
    FREE: t('upgrade.tiers.free'),
    PLUS: t('upgrade.tiers.plus'),
    PRO: t('upgrade.tiers.pro'),
    CENTER: t('upgrade.tiers.center'),
  };
  // Annual mode reads like the landing (Johnny, 2026-09-05): the per-month
  // equivalent leads, and the yearly total — the number the next screen's QR
  // encodes — stays printed right under it with the saving, so nothing that
  // is charged is ever hidden.
  const priceFor = (code: PurchasableCode): { main: string; note: string | null; offerLine: string | null } => {
    const base =
      cycle === 'ANNUAL'
        ? {
            main: t('upgrade.price.annualPerMonth', { amount: fmtVnd(perMonthFromAnnualVnd(PRICE_VND_ANNUAL[code]), i18n.language) }),
            note: t('upgrade.price.annualNote', {
              save: fmtVnd(PRICE_VND_MONTHLY[code] * 12 - PRICE_VND_ANNUAL[code], i18n.language),
              total: fmtVnd(PRICE_VND_ANNUAL[code], i18n.language),
            }),
          }
        : { main: t('upgrade.price.perMonth', { amount: fmtVnd(PRICE_VND_MONTHLY[code], i18n.language) }), note: null };
    // THI-200: the discounted first-term line always goes through
    // applyPercentOff against the full list price for the picked cycle — never
    // computed inline (INVARIANTS §1.5).
    const listVnd = cycle === 'ANNUAL' ? PRICE_VND_ANNUAL[code] : PRICE_VND_MONTHLY[code];
    const line = offerLineFor(code);
    const offerLine = line
      ? t('offer.firstTerm', { amount: fmtVnd(applyPercentOff(listVnd, line.pct).amountVnd, i18n.language), name: line.name })
      : null;
    return { ...base, offerLine };
  };
  const tierPrice: Record<TierCode, { main: string; note: string | null; offerLine: string | null }> = {
    FREE: { main: t('upgrade.price.free'), note: null, offerLine: null },
    PLUS: priceFor('PLUS'),
    PRO: priceFor('PRO'),
    CENTER: priceFor('CENTER'),
  };
  const rowLabel: Record<RowKey, string> = {
    students: t('upgrade.rows.students'),
    seats: t('upgrade.rows.seats'),
    core: t('upgrade.rows.core'),
    noWatermark: t('upgrade.rows.noWatermark'),
    qrPayment: t('upgrade.rows.qrPayment'),
    autoReconcile: t('upgrade.rows.autoReconcile'),
    bookkeeping: t('upgrade.rows.bookkeeping'),
    aiCredits: t('upgrade.rows.aiCredits'),
    aiImport: t('upgrade.rows.aiImport'),
    sessionNotes: t('upgrade.rows.sessionNotes'),
    progressLink: t('upgrade.rows.progressLink'),
    payroll: t('upgrade.rows.payroll'),
    zns: t('upgrade.rows.zns'),
    multiBranch: t('upgrade.rows.multiBranch'),
  };

  const textValues: Record<TextRowKey, Record<TierCode, string>> = {
    students: { FREE: '10', PLUS: t('unlimited'), PRO: t('unlimited'), CENTER: t('unlimited') },
    seats: { FREE: '1', PLUS: '2', PRO: '5', CENTER: '20' },
    autoReconcile: { FREE: t('upgrade.rows.autoReconcileManual'), PLUS: '60', PRO: '150', CENTER: '500' },
    aiCredits: { FREE: '30', PLUS: '500', PRO: '2000', CENTER: '5000' },
  };

  // Three DISTINCT cell states, and they must stay distinct: a check glyph, an
  // X glyph, and a chip. Collapsing them into differently-coloured text is
  // exactly what a compare table must not do.
  function renderCell(rowKey: RowKey, code: TierCode): ReactNode {
    if (rowKey === 'students' || rowKey === 'seats' || rowKey === 'autoReconcile' || rowKey === 'aiCredits') {
      return textValues[rowKey][code];
    }
    if (rowKey === 'zns') {
      const status = COMING_SOON_ROWS[rowKey][code];
      if (status === 'off') return <X className="w-4 h-4 text-surface-400" strokeWidth={2} />;
      return (
        <span className="text-[11px] font-medium text-attention-foreground bg-attention-tint px-1.5 py-0.5 rounded whitespace-nowrap">
          {t('upgrade.comingSoon')}
        </span>
      );
    }
    const on = BOOLEAN_ROWS[rowKey][code];
    return on ? (
      <Check className="w-4 h-4 text-brand-500" strokeWidth={2.5} />
    ) : (
      <X className="w-4 h-4 text-surface-400" strokeWidth={2} />
    );
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{t('upgrade.title')}</DialogTitle>
          <DialogDescription>{t('upgrade.subtitle')}</DialogDescription>
        </DialogHeader>

        {offer && (
          <p className="rounded-sm bg-brand-50 px-3 py-2 text-[12.5px] leading-[1.5] text-brand-700">
            {t('offer.header', {
              name: offer.name,
              pct: offer.percentOff,
              deadline: new Date(offer.deadline).toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }),
            })}
          </p>
        )}

        <div className="flex items-center justify-center gap-2">
          <div className="inline-flex rounded-md border border-surface-200 p-0.5 bg-surface-50">
            {(['MONTHLY', 'ANNUAL'] as const).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  setPickedCycle(c);
                  // A stale PROMO_PLAN/CYCLE note from the previous cycle must
                  // not linger; PromoCodeRow re-checks the code for the new one.
                  setPromoError(null);
                }}
                className={cn(
                  'px-3 py-1 text-[12px] font-medium rounded transition-colors',
                  cycle === c ? 'bg-brand-500 text-white' : 'text-surface-600 hover:text-surface-800',
                )}
              >
                {t(`upgrade.cycle.${c === 'MONTHLY' ? 'monthly' : 'annual'}`)}
              </button>
            ))}
          </div>
          {cycle === 'ANNUAL' && (
            <span className="text-[12px] font-medium text-brand-700 bg-brand-50 px-1.5 py-0.5 rounded">
              {t('upgrade.annualSave')}
            </span>
          )}
        </div>

        <div className="overflow-x-auto -mx-1 px-1">
          <div className="grid grid-cols-[1.5fr_repeat(4,1fr)] min-w-[560px]">
            {/* Tier header row */}
            <div />
            {TIER_CODES.map((code) => (
              <div
                key={code}
                className={cn(
                  'px-2 pb-3 text-center border-b',
                  code === currentCode ? 'border-brand-500 bg-brand-500/6' : 'border-surface-200',
                )}
              >
                <div className="flex items-center justify-center gap-[6px]">
                  <div className="text-[13px] font-semibold text-surface-800">{tierLabel[code]}</div>
                  {/* THI-200 follow-up — same chip style as the "Sắp có" badge
                      below (COMING_SOON_ROWS), brand-toned for a discount
                      rather than amber for a roadmap item. */}
                  {code !== 'FREE' && offerLineFor(code as PurchasableCode) && (
                    <span className="inline-flex h-[18px] shrink-0 items-center whitespace-nowrap rounded-[6px] bg-brand-50 px-[6px] text-[10px] font-medium text-brand-700">
                      −{offerLineFor(code as PurchasableCode)!.pct}% kỳ đầu
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-surface-500 mt-0.5">{tierPrice[code].main}</div>
                {tierPrice[code].note && (
                  <div className="text-[10.5px] leading-[1.35] text-surface-400 mt-0.5">{tierPrice[code].note}</div>
                )}
                {tierPrice[code].offerLine && (
                  <div className="text-[10.5px] leading-[1.35] font-semibold text-brand-600 mt-0.5">
                    {tierPrice[code].offerLine}
                  </div>
                )}
                {code === currentCode && (
                  <div className="mt-1 text-[11px] font-medium text-brand-700">
                    {onTrial ? t('planBadge.trial') : t('upgrade.currentPlan')}
                  </div>
                )}
              </div>
            ))}

            {/* Feature rows */}
            {ROW_ORDER.map((rowKey) => {
              const isHighlighted = rowKey === highlightedRow;
              return (
                <Fragment key={rowKey}>
                  <div
                    className={cn(
                      // Both border colors here must be DIRECTIONAL. In twMerge's
                      // conflict graph a bare border-{color} conflicts with every
                      // directional border-{side}-{color} that precedes it, so a
                      // bare border-surface-100 (wanted only for the bottom
                      // hairline) would silently drop border-l-transparent and
                      // paint a 2px surface-100 rule down the label column. The
                      // highlighted branch still works: its bare border-brand-500
                      // claims the left edge, then its border-b-surface-100
                      // reclaims the bottom.
                      'py-2 pl-3 pr-2 flex items-center text-[13px] border-l-2 border-l-transparent border-b border-b-surface-100',
                      isHighlighted
                        ? 'font-semibold text-brand-700 bg-brand-500/6 border-brand-500 border-b-surface-100'
                        : 'text-surface-600',
                    )}
                  >
                    {rowLabel[rowKey]}
                  </div>
                  {TIER_CODES.map((code) => (
                    <div
                      key={code}
                      className={cn(
                        'py-2 px-2 flex items-center justify-center text-[13px] text-surface-700 border-b border-surface-100',
                        // Two independent reasons for the same tint, and it must
                        // be the SAME tint: the current-plan column runs the
                        // full height per mock 3c (it used to stop at the header
                        // row, so the column read as an unfinished stripe), and
                        // a feature-highlighted row spans all four columns. One
                        // class either way — never two stacked alphas.
                        (isHighlighted || code === currentCode) && 'bg-brand-500/6',
                      )}
                    >
                      {renderCell(rowKey, code)}
                    </div>
                  ))}
                </Fragment>
              );
            })}

            {/* CTA row */}
            <div className="pt-3" />
            {TIER_CODES.map((code) => {
              const cta = ctaFor(code);
              return (
                <div
                  key={code}
                  className={cn(
                    'pt-3 flex items-center justify-center',
                    // The tint reaches the button row too — 3c ends the column
                    // at the CTA, not at the last feature row.
                    code === currentCode && 'bg-brand-500/6',
                  )}
                >
                  {cta === 'buy' && (
                    /* Mock A2 `3f`, row "Nút mua gói": <Button className="min-h-[44px]">.
                       size="sm" is h-8 → 25.6px at --spacing 0.2rem, well under
                       the touch minimum and visibly smaller than the design. */
                    <Button
                      className="min-h-[44px]"
                      disabled={upgrade.isPending}
                      onClick={() => handleUpgrade(code as PurchasableCode)}
                    >
                      {onTrial ? t('upgrade.ctaChoose') : t('upgrade.cta')}
                    </Button>
                  )}
                  {cta === 'current' && (
                    <span className="text-[12px] text-surface-400">{t('upgrade.currentPlan')}</span>
                  )}
                  {/* FREE: no CTA — you can't "buy" Free. */}
                </div>
              );
            })}
          </div>
        </div>

        {showPromoRow && (
          <div className="flex flex-col gap-[6px]">
            <PromoCodeRow
              planCode={contextPlan}
              billingCycle={cycle}
              value={activePromo ? { code: activePromo.code, check: activePromo.check } : null}
              onAccepted={(code, checkResult) => {
                setPromo({ code, check: checkResult });
                setPromoError(null);
              }}
              onCleared={() => {
                setPromo(null);
                setPromoError(null);
              }}
            />
            {/* Fix wave (Important #1): a PROMO_* rejection from the upgrade
                mutation (e.g. the accepted code does not cover the tier just
                clicked) renders here, next to the code — not in the generic
                error block below, which is for unrelated failures. */}
            {promoError && <p className="text-[12.5px] text-red-700">{promoError}</p>}
          </div>
        )}

        {/* The premium band is a conversation, not a checkout — and as a footer
            row it costs the table no width. Direct Zalo OA link rather than a
            hop to /lien-he: in the app, speed wins. */}
        <div className="mt-4 flex flex-col gap-3 border-t border-surface-200 pt-[14px] sm:flex-row sm:items-center sm:justify-between sm:gap-5">
          <p className="text-[12.5px] leading-[1.55] text-surface-500">{t('upgrade.enterprise.body')}</p>
          <a
            href={ZALO_OA_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-[44px] shrink-0 items-center text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            {t('upgrade.enterprise.cta')}
          </a>
        </div>

        {scheduled && (
          <p className="rounded-sm bg-brand-50 px-3 py-2 text-[13px] text-brand-800">
            {t('scheduledChange', {
              plan: scheduled.planName,
              date: new Date(scheduled.startsAt).toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }),
            })}
          </p>
        )}

        {onPaidTerm && !scheduled && (
          <p className="text-[12px] text-surface-500">{t('prorationHint')}</p>
        )}

        {error && (
          <p className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700">{error}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
